import { startOfDay } from 'date-fns';
import { atom, useAtomValue } from 'jotai';
import { atomFamily } from 'jotai-family';
import { unwrap } from 'jotai/utils';
import { useMemo } from 'react';
import { OwnershipTimelineEvent, OwnershipTimelineStack } from '../../../../schemas/OwnershipTimeline.schema';
import { fetchOwnershipTimeline } from '../../../../services/queries/MaggieMetricsQueries';
import { plainDateToZonedDateTime } from '../../../../util/date-utilts';
import { FMT } from '../../../../util/formatter-service';
import { captureUnexpectedState } from '../../../../util/SentryUtils';

export interface IRoundEntry {
  key: string;
  date: Date;
  roundId: number | null;
  roundLabel: string;
  isCurrent: boolean;
}

export interface IStackedBarPosition {
  position: string;
  shares: number;
  ownershipPct: number;
  /** Drives the chart's `value` view mode. */
  fmv: number;
}

export interface IStackedBar {
  key: string;
  positions: IStackedBarPosition[];
}

export const CURRENT_ROUND_KEY = 'current';

// `null` means "Current" (today).
export const ownershipTimelineAsOfDateJ = atomFamily((_companyId: number) => atom<Date | null>(null));

export const ownershipTimelineEventsJ = atomFamily((companyId: number) =>
  atom<Promise<OwnershipTimelineEvent[]>>(async () => {
    if (companyId <= 0) {
      return [];
    }
    try {
      return await fetchOwnershipTimeline(companyId);
    } catch (err) {
      captureUnexpectedState('ownershipTimelineEventsJ: ownership-timeline fetch failed', { companyId, err });
      return [];
    }
  })
);

export const ownershipTimelineEventsSyncJ = atomFamily((companyId: number) =>
  unwrap(ownershipTimelineEventsJ(companyId), (prev) => prev ?? [])
);

// The last (today's) event is the synthetic "Current".
function isCurrentIndex(events: OwnershipTimelineEvent[], index: number): boolean {
  return index === events.length - 1;
}

export const roundsForCompanyJ = atomFamily((companyId: number) =>
  atom<Promise<IRoundEntry[]>>(async (get) => {
    const events = await get(ownershipTimelineEventsJ(companyId));
    if (events.length === 0) {
      return [
        {
          key: CURRENT_ROUND_KEY,
          date: startOfDay(new Date()),
          roundId: null,
          roundLabel: 'Current',
          isCurrent: true,
        },
      ];
    }
    return events.map((e, i) => {
      const isCurrent = isCurrentIndex(events, i);
      const date = plainDateToZonedDateTime(e.date);
      return {
        key: isCurrent ? CURRENT_ROUND_KEY : e.date,
        date,
        roundId: e.roundIds[0] ?? null,
        roundLabel: isCurrent ? 'Current' : FMT.format('dateNumeric', e.date),
        isCurrent,
      };
    });
  })
);

// Sync view of the rounds list. Keeps `selectedRoundJ` sync so the picker doesn't
// suspend on date changes — a suspended re-render would freeze the Select's listbox.
export const roundsForCompanySyncJ = atomFamily((companyId: number) =>
  unwrap(roundsForCompanyJ(companyId), (prev) => prev ?? [])
);

export const selectedRoundJ = atomFamily((companyId: number) =>
  atom<IRoundEntry | null>((get) => {
    const rounds = get(roundsForCompanySyncJ(companyId));
    if (rounds.length === 0) {
      return null;
    }
    const asOfDate = get(ownershipTimelineAsOfDateJ(companyId));
    const currentEntry = rounds.find((r) => r.isCurrent) ?? null;

    if (asOfDate === null) {
      return currentEntry;
    }
    if (asOfDate.getTime() >= startOfDay(new Date()).getTime()) {
      return currentEntry;
    }

    const target = asOfDate.getTime();
    let best: IRoundEntry | null = null;
    for (const r of rounds) {
      if (r.isCurrent) {
        continue;
      }
      if (r.date.getTime() <= target) {
        best = r;
      }
    }
    return best;
  })
);

// Events are pre-cumulated by the backend, so holdings as of a date = the latest event on
// or before it. `null`/today/future selects the current (last) event.
export function selectEventAsOf(
  events: OwnershipTimelineEvent[],
  asOfDate: Date | null
): OwnershipTimelineEvent | null {
  if (events.length === 0) {
    return null;
  }
  if (asOfDate === null || asOfDate.getTime() >= startOfDay(new Date()).getTime()) {
    return events[events.length - 1];
  }
  const target = asOfDate.getTime();
  let best: OwnershipTimelineEvent | null = null;
  for (const e of events) {
    if (plainDateToZonedDateTime(e.date).getTime() <= target) {
      best = e;
    } else {
      break;
    } // events are ascending
  }
  return best;
}

export function selectStacksAsOf(
  events: OwnershipTimelineEvent[],
  asOfDate: Date | null
): OwnershipTimelineStack[] {
  return selectEventAsOf(events, asOfDate)?.stacks ?? [];
}

// Reads the async events atom (suspends only on the initial fetch) and selects in-memory,
// so stepping the as-of-date never re-suspends the panel.
export function useHoldingsAsOfDate(companyId: number): OwnershipTimelineStack[] {
  const events = useAtomValue(ownershipTimelineEventsJ(companyId));
  const asOfDate = useAtomValue(ownershipTimelineAsOfDateJ(companyId));
  return useMemo(() => selectStacksAsOf(events, asOfDate), [events, asOfDate]);
}

export const stackedBarsJ = atomFamily((companyId: number) =>
  atom<Promise<IStackedBar[]>>(async (get) => {
    const events = await get(ownershipTimelineEventsJ(companyId));
    return events.map((e, i) => ({
      key: isCurrentIndex(events, i) ? CURRENT_ROUND_KEY : e.date,
      positions: aggregateByPosition(e.stacks),
    }));
  })
);

// Chart series group by position name; multiple restructures can share a position.
function aggregateByPosition(stacks: OwnershipTimelineStack[]): IStackedBarPosition[] {
  const byPosition = new Map<string, IStackedBarPosition>();
  for (const s of stacks) {
    const position = s.position ?? s.label;
    const existing = byPosition.get(position) ?? { position, shares: 0, ownershipPct: 0, fmv: 0 };
    existing.shares += s.shares;
    existing.fmv += s.fmv;
    // API ownershipPct is a fraction; the chart's percent formatters expect percent units.
    existing.ownershipPct += (s.ownershipPct ?? 0) * 100;
    byPosition.set(position, existing);
  }
  return Array.from(byPosition.values());
}
